// The DFT module's formation energy of a defect (D-3): its charge-state runs against the host reference, the
// chemical potentials from the element references (rich / poor limits of a host of two elements, impurities below
// their references), the image-charge correction of charged states (screened point charge and potential alignment,
// with the dielectric constant), the formation energy against the Fermi level and the transition levels.
import { Alert, Badge, Checkbox, Group, List, NumberInput, Radio, Select, Stack, Table, Text } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { ErrorBox } from "../../components/Errors";
import { PALETTE, Plot } from "../../components/Plot";
import { defectFormation, formationAt, formationSetup, type FormationBody, type FormationResult, type FormationSetup } from "../../lib/qe";

const GRID = "#e9e9e6";
const sign = (q: number) => (q > 0 ? `+${q}` : `${q}`);

/** The formation energy against the Fermi level: a line per charge state, the lowest thick, the transition levels. */
export function formationFigure(r: FormationResult) {
  const gap = r.gap_eV;
  const data: any[] = r.charges.map((c, i) => ({
    x: [0, gap], y: [formationAt(c, 0), formationAt(c, gap)], type: "scatter", mode: "lines", name: `q = ${sign(c.q)}`,
    line: { color: PALETTE[i % PALETTE.length], width: 1.2, dash: "dot" },
    hovertemplate: `q = ${sign(c.q)}<br>E<sub>F</sub> = %{x:.2f} eV<br>E<sub>f</sub> = %{y:.2f} eV<extra></extra>`,
  }));
  const byQ = Object.fromEntries(r.charges.map((c) => [c.q, c]));
  const xs: number[] = [], ys: number[] = [];
  for (const s of r.segments) {
    xs.push(s.from_eV, s.to_eV);
    ys.push(formationAt(byQ[s.q], s.from_eV), formationAt(byQ[s.q], s.to_eV));
  }
  data.push({ x: xs, y: ys, type: "scatter", mode: "lines", name: "lowest", line: { color: "#222222", width: 3 }, hoverinfo: "skip" });
  if (r.transition_levels.length)
    data.push({ x: r.transition_levels.map((t) => t.energy_eV), y: r.transition_levels.map((t) => formationAt(byQ[t.from_q], t.energy_eV)),
      type: "scatter", mode: "markers+text", name: "transition levels", marker: { size: 8, color: "#222222" },
      text: r.transition_levels.map((t) => t.label), textposition: "top center", hoverinfo: "text",
      hovertext: r.transition_levels.map((t) => `${t.label}: E_VBM + ${t.energy_eV.toFixed(2)} eV (E_CBM − ${t.below_cbm_eV.toFixed(2)})`) });
  const all = r.charges.flatMap((c) => [formationAt(c, 0), formationAt(c, gap)]);
  const lo = Math.min(...ys, 0), hi = Math.max(...ys) + 1.5;
  const layout = {
    xaxis: { title: { text: "E<sub>F</sub> − E<sub>VBM</sub> (eV)" }, range: [0, gap], gridcolor: GRID, zeroline: false },
    yaxis: { title: { text: "Formation energy (eV)" }, range: [Math.max(lo - 0.5, Math.min(...all) - 0.5), hi], gridcolor: GRID },
    legend: { orientation: "h", x: 1, xanchor: "right", y: 1.12 }, margin: { l: 60, r: 20, t: 40, b: 50 },
  };
  return { data, layout };
}

/** The default choice: the newest run of each charge state, the newest host and element references. */
export function defaults(s: FormationSetup) {
  const perQ: Record<number, string> = {};
  for (const c of s.charges) if (c.q != null && !(c.q in perQ)) perQ[c.q] = c.job_id;
  const refs: Record<string, string> = {};
  for (const el of s.needed) if (s.elements[el]?.length) refs[el] = s.elements[el][0].job_id;
  const hostEls = Object.keys(s.host_composition).sort();
  return { jobs: Object.values(perQ), refs, rich: hostEls.length === 2 ? hostEls[0] : null };
}

export function DefectFormationView({ jobId }: { jobId: string }) {
  const setup = useQuery({ queryKey: ["qe-formation-setup", jobId], queryFn: () => formationSetup(jobId), retry: false });
  const s = setup.data;
  return (
    <Stack gap="xs" data-testid="defect-formation">
      <ErrorBox error={setup.error} />
      {s && <FormationForm s={s} key={jobId} />}
    </Stack>
  );
}

function FormationForm({ s }: { s: FormationSetup }) {
  const d0 = useMemo(() => defaults(s), [s]);
  const [jobs, setJobs] = useState<string[]>(d0.jobs);
  const [host, setHost] = useState<string | null>(s.hosts[0]?.job_id ?? null);
  const [refs, setRefs] = useState<Record<string, string>>(d0.refs);
  const [rich, setRich] = useState<string | null>(d0.rich);
  const [delta, setDelta] = useState<Record<string, number>>({});
  const [eps, setEps] = useState<number | null>(null);
  const hostEls = Object.keys(s.host_composition).sort();
  const body: FormationBody = { job_ids: jobs, host_job: host, references: refs, epsilon: eps, rich, delta };
  const [debounced] = useDebouncedValue(body, 500);
  const q = useQuery({ queryKey: ["qe-formation", JSON.stringify(debounced)], queryFn: () => defectFormation(debounced),
    enabled: debounced.job_ids.length > 0 && !!debounced.host_job, retry: false, staleTime: 60_000 });
  const r = q.data;
  const fig = useMemo(() => (r ? formationFigure(r) : null), [r]);
  const charged = s.charges.some((c) => jobs.includes(c.job_id) && c.q);
  // a host of more than two elements: all but its last element below their references; impurities likewise
  const others = [...(hostEls.length > 2 ? hostEls.slice(0, -1) : []), ...s.impurities];
  return (
    <>
      <Group gap={6}>
        <Text size="sm" fw={600}>Defect {s.label}</Text>
        <Badge variant="light" color="gray">{s.dimensionality}</Badge>
        {r && r.transition_levels.map((t) => <Badge key={t.label} variant="light" color="teal">{t.label} at E_VBM + {t.energy_eV.toFixed(2)} eV</Badge>)}
      </Group>
      {s.charges.length === 0 && <Alert color="orange" p="xs">No finished run of this defect in the project.</Alert>}
      <Text size="xs" fw={600}>Charge states</Text>
      <Group gap="md" data-testid="formation-charges">
        {[...s.charges].sort((a, b) => (b.q ?? 0) - (a.q ?? 0)).map((c) => (
          <Checkbox key={c.job_id} size="xs" checked={jobs.includes(c.job_id)} label={`q = ${sign(c.q ?? 0)}${s.charges.filter((x) => x.q === c.q).length > 1 ? ` (${c.name})` : ""}`}
            onChange={(e) => setJobs((j) => (e.currentTarget.checked ? [...j.filter((x) => s.charges.find((y) => y.job_id === x)?.q !== c.q), c.job_id]
              : j.filter((x) => x !== c.job_id)))} />
        ))}
      </Group>
      <Group gap="xs" align="flex-end">
        <Select size="xs" w={280} label="Host reference" data={s.hosts.map((h) => ({ value: h.job_id, label: h.name ?? h.job_id }))}
          value={host} onChange={setHost} placeholder="none in the project" allowDeselect={false} />
        <NumberInput size="xs" w={200} label="Dielectric constant (static)" placeholder={charged ? "needed for charged states" : "for charged states"}
          value={eps ?? ""} min={1} max={1000} step={0.5} decimalScale={3}
          onChange={(v) => setEps(typeof v === "number" && v >= 1 ? v : null)} data-testid="formation-eps" />
        <Text size="xs" c="dimmed" maw={360}>e.g. Si 11.7, GaAs 12.9, GaN 9.5, ZnO 8.8, MgO 9.8, diamond 5.7 (experiment)</Text>
      </Group>
      <Text size="xs" fw={600}>Chemical potentials</Text>
      <Group gap="xs" align="flex-end" data-testid="formation-refs">
        {s.needed.map((el) => (
          <Select key={el} size="xs" w={210} label={`${el} reference`} placeholder={s.elements[el]?.length ? "choose" : "none in the project"}
            data={(s.elements[el] ?? []).map((x) => ({ value: x.job_id, label: x.name ?? x.job_id }))} value={refs[el] ?? null}
            onChange={(v) => setRefs((m) => { const n = { ...m }; if (v) n[el] = v; else delete n[el]; return n; })} clearable />
        ))}
      </Group>
      {hostEls.length === 2 && (
        <Radio.Group value={rich ?? ""} onChange={setRich} label={<Text size="xs">Conditions</Text>}>
          <Group gap="md">{hostEls.map((el) => <Radio key={el} size="xs" value={el} label={`${el}-rich`} />)}</Group>
        </Radio.Group>
      )}
      {others.length > 0 && <Group gap="xs" align="flex-end">
        {others.map((el) => (
          <NumberInput key={el} size="xs" w={160} label={`Δμ ${el} (eV, ≤ 0)`} value={delta[el] ?? 0} max={0} step={0.1} decimalScale={3}
            onChange={(v) => typeof v === "number" && setDelta((m) => ({ ...m, [el]: v }))} />
        ))}
        <Text size="xs" c="dimmed">below the element's reference (0: the element's rich limit; other phases not checked)</Text>
      </Group>}
      <ErrorBox error={q.error} />
      {r && <>
        {r.warnings.length > 0 && <Alert color="orange" p="xs"><List size="xs">{r.warnings.map((w) => <List.Item key={w}>{w}</List.Item>)}</List></Alert>}
        {r.gap_eV > 0 ? fig && <Plot data={fig.data} layout={fig.layout} height={400} />
          : <Text size="sm" data-testid="formation-metal">A metallic host: E_f = {r.charges.map((c) => c.formation_at_vbm_eV.toFixed(3)).join(", ")} eV
            (no gap, so no dependence on the Fermi level).</Text>}
        <Text size="xs" c="dimmed">E_f(q, E_F) = E_D(q) − E_H − Σ n_i μ_i + q (E_VBM + E_F) + E_corr(q); E_F from the host's valence top to its
          conduction bottom ({r.gap_eV.toFixed(2)} eV along the path: a semi-local gap is too small, so are the levels near the conduction band).
          {r.formation_enthalpy_eV != null ? ` Host formation enthalpy ${r.formation_enthalpy_eV.toFixed(3)} eV per cell.` : ""}</Text>
        <Table fz="xs" verticalSpacing={2} data-testid="formation-table">
          <Table.Thead><Table.Tr><Table.Th>q</Table.Th><Table.Th>Atoms</Table.Th><Table.Th>E_f at VBM (eV)</Table.Th><Table.Th>at CBM</Table.Th>
            <Table.Th>−Σ n μ</Table.Th><Table.Th>q E_VBM</Table.Th><Table.Th>E_corr (E_PC, alignment)</Table.Th></Table.Tr></Table.Thead>
          <Table.Tbody>
            {r.charges.map((c) => (
              <Table.Tr key={c.q}>
                <Table.Td>{sign(c.q)}</Table.Td>
                <Table.Td>{Object.entries(c.added).map(([el, n]) => `${n > 0 ? "+" : ""}${n} ${el}`).join(", ") || "–"}</Table.Td>
                <Table.Td>{c.formation_at_vbm_eV.toFixed(3)}</Table.Td>
                <Table.Td>{c.formation_at_cbm_eV.toFixed(3)}</Table.Td>
                <Table.Td>{c.chemical_eV.toFixed(3)}</Table.Td>
                <Table.Td>{c.q_vbm_eV.toFixed(3)}</Table.Td>
                <Table.Td>{c.q ? `${c.correction.e_corr_eV.toFixed(3)} (${c.correction.e_pc_eV.toFixed(3)}, ${c.correction.alignment_V >= 0 ? "+" : ""}${c.correction.alignment_V.toFixed(3)} V on ${c.correction.sampled} atoms)` : "–"}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
        {Object.keys(r.chemical_potentials).length > 0 && <Text size="xs">μ: {Object.entries(r.chemical_potentials).map(([el, m]) =>
          `${el} ${m.mu.toFixed(3)} eV${m.delta != null ? ` (Δμ ${m.delta >= 0 ? "+" : ""}${m.delta.toFixed(3)})` : ""}, from ${m.from}`).join("; ")}</Text>}
      </>}
    </>
  );
}
